import { useQuery } from '@tanstack/react-query';
import { apiClient } from './api-client';

const GROUP_MEMBERS_QUERY_KEY = ['integration-group-members'];

type IntegrationGroup = {
  groupId: string;
  memberIntegrationIds?: string[];
};

/**
 * Group id to the integrations it contains.
 *
 * Entitlements carry integration groups as `group:<uuid>` references rather
 * than as member ids, so a resource check against a real integration id has to
 * expand them first. The list is the same one the filters read: it requires
 * `Integration:Read`, and the handler narrows the groups to the caller's own
 * read reach. `integration-triage` implies `integration-data`, which carries
 * `Integration:Read` at the same scope, so a role that can triage can read the
 * groups its triage grant names.
 *
 * Session-stable like the entitlements it complements: a group's membership
 * follows the account's tags, so it changes on the next load rather than
 * mid-session, and enforcement is server-side either way.
 */
export const useIntegrationGroupMembers = ({
  enabled = true,
}: { enabled?: boolean } = {}) =>
  useQuery({
    enabled,
    queryKey: GROUP_MEMBERS_QUERY_KEY,
    queryFn: async (): Promise<Record<string, string[]>> => {
      const response = await apiClient.get<{ data: IntegrationGroup[] }>(
        '/api/core/tenant/integration-groups',
      );
      return Object.fromEntries(
        (response.data.data ?? []).map((group) => [
          group.groupId,
          group.memberIntegrationIds ?? [],
        ]),
      );
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
